import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ZoomLevel = 0.8 | 0.9 | 1.0;

interface ZoomContextType {
  zoomLevel: ZoomLevel;
  setZoomLevel: (level: ZoomLevel) => void;
  zoomPercentage: number; // For display purposes (80, 90, 100)
}

const ZoomContext = createContext<ZoomContextType | undefined>(undefined);

export const useZoom = () => {
  const context = useContext(ZoomContext);
  if (!context) {
    throw new Error('useZoom must be used within ZoomProvider');
  }
  return context;
};

interface ZoomProviderProps {
  children: ReactNode;
}

const ZOOM_STORAGE_KEY = 'app-zoom-level';
const DEFAULT_ZOOM: ZoomLevel = 1.0; // 100%

export const ZoomProvider: React.FC<ZoomProviderProps> = ({ children }) => {
  const [zoomLevel, setZoomLevelState] = useState<ZoomLevel>(() => {
    // Load from localStorage on mount
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(ZOOM_STORAGE_KEY);
        if (saved) {
          const parsed = parseFloat(saved);
          // Validate that it's one of our allowed values
          if (parsed === 0.8 || parsed === 0.9 || parsed === 1.0) {
            return parsed as ZoomLevel;
          }
        }
      } catch (error) {
        console.error('Failed to load zoom level from localStorage:', error);
      }
    }
    return DEFAULT_ZOOM;
  });

  // Save to localStorage whenever zoom level changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(ZOOM_STORAGE_KEY, zoomLevel.toString());
      } catch (error) {
        console.error('Failed to save zoom level to localStorage:', error);
      }
    }
  }, [zoomLevel]);

  const setZoomLevel = (level: ZoomLevel) => {
    setZoomLevelState(level);
  };

  const zoomPercentage = Math.round(zoomLevel * 100);

  return (
    <ZoomContext.Provider value={{ zoomLevel, setZoomLevel, zoomPercentage }}>
      {children}
    </ZoomContext.Provider>
  );
};
